import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { defaultCountries, getCountry } from "react-international-phone";
import PhoneInput from "../PhoneInput";
import PhoneOtpDialog from "../PhoneOtpDialog";

// Twilio requires E.164: leading "+", country code, then up to 14 more digits.
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

const toLibraryCountry = (code) => {
  if (!code) return "us";
  const c = String(code).toUpperCase();
  return c === "UK" ? "gb" : c.toLowerCase();
};

/**
 * Build an E.164 number from the (countryCode, nationalDigits) pair the forms hold.
 * Uses the library's full country list so this works beyond the handful of
 * dial codes hardcoded in PhoneInput.
 */
export const buildE164 = (countryCode, phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";

  // getCountry returns an already-parsed country ({ name, iso2, dialCode, ... }),
  // or undefined for an unknown iso2 — do not pass it through parseCountry.
  const country = getCountry({
    field: "iso2",
    value: toLibraryCountry(countryCode),
    countries: defaultCountries,
  });
  const dialCode = country?.dialCode || "";
  if (!dialCode) return "";

  return `+${dialCode}${digits}`;
};

/**
 * PhoneInput plus SMS OTP verification (Twilio Verify).
 *
 * Drop-in replacement for PhoneInput — it accepts the same props and forwards
 * them through, so existing call sites only need the component name swapped.
 *
 * Verification state is held internally and keyed on the E.164 number, so
 * editing the number after verifying automatically clears the verified badge.
 *
 * @param {function} onVerifiedChange - Optional (isVerified, e164) callback, for
 *   forms that want to gate submission on a verified number.
 * @param {boolean} showVerify - Set false to render a plain PhoneInput. Use this
 *   for numbers belonging to someone other than the person at the keyboard (an
 *   admin entering a user's number, a secondary contact) — they cannot receive
 *   the SMS, so offering the button would only mislead.
 * @param {string} verifiedE164 - A number the parent already considers verified.
 *   Restores the badge after a remount, and lets a form grandfather a number
 *   that was already on file so existing users aren't forced to re-verify.
 */
const PhoneVerify = ({
  countryCode,
  phone,
  onChange,
  onPhoneChange,
  onVerifiedChange,
  showVerify = true,
  verifiedE164 = "",
  disabled = false,
  ...rest
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verifiedNumber, setVerifiedNumber] = useState(verifiedE164 || "");

  // Adopt a late-arriving parent value (forms that hydrate async) without
  // clobbering a number the user just verified here.
  useEffect(() => {
    if (verifiedE164) setVerifiedNumber((prev) => prev || verifiedE164);
  }, [verifiedE164]);
  // The library reports the exact E.164 on change (and on mount); prefer it,
  // and fall back to building one from the props for never-touched fields.
  const [liveE164, setLiveE164] = useState("");

  const derivedE164 = useMemo(() => buildE164(countryCode, phone), [countryCode, phone]);
  const currentE164 = liveE164 || derivedE164;

  const isValid = E164_PATTERN.test(currentE164);
  const isVerified = Boolean(currentE164) && verifiedNumber === currentE164;

  // Keep the callback in a ref so parents don't need to memoize it.
  const onVerifiedChangeRef = useRef(onVerifiedChange);
  onVerifiedChangeRef.current = onVerifiedChange;

  useEffect(() => {
    if (onVerifiedChangeRef.current) {
      onVerifiedChangeRef.current(isVerified, currentE164);
    }
  }, [isVerified, currentE164]);

  const handleChange = useCallback(
    (phoneValue, meta) => {
      setLiveE164(phoneValue || "");
      if (onChange) onChange(phoneValue, meta);
    },
    [onChange]
  );

  const handleVerified = useCallback((verifiedE164) => {
    setVerifiedNumber(verifiedE164);
    setDialogOpen(false);
  }, []);

  return (
    <>
      <PhoneInput
        countryCode={countryCode}
        phone={phone}
        onChange={handleChange}
        onPhoneChange={onPhoneChange}
        disabled={disabled}
        {...rest}
      />

      {showVerify && (
        <div className="flex items-center gap-2 mt-1">
          {isVerified ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
              <Icon icon="lucide:check-circle-2" className="h-3.5 w-3.5" />
              Phone verified
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              disabled={disabled || !isValid}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-blue hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
            >
              <Icon icon="lucide:shield-check" className="h-4 w-4" />
              Verify with OTP
            </button>
          )}
        </div>
      )}

      {/* Portalled to body: most call sites sit inside a <form>, and Dialog's
          close button has no explicit type — nested, it would submit the form. */}
      {dialogOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <PhoneOtpDialog
            isOpen={dialogOpen}
            phone={currentE164}
            onClose={() => setDialogOpen(false)}
            onVerified={handleVerified}
          />,
          document.body
        )}
    </>
  );
};

export default PhoneVerify;
